import { useFormField } from '@calendly/form'
import { FieldError, FieldLabel, useErrorId } from '@calendly/form/parts'
import { ControlledRichTextEditor } from '@calendly/rich-text-editor'
import { useCallback } from 'react'

import { InsertLinkPopup, useSimpleLinkPopup } from 'components/core/insert_link_popup'
import { BoxWithLinkTooltip, useSimpleLinkTooltip } from 'components/core/link_tooltip'

export const RichTextField = ({
  label, proTip, id, className = null, isRequired, useNarrowTooltip = false, hideLabel = false, ...props
}) => {
  const [fieldProps, meta, { setValue, setTouched, setError }] = useFormField(props)
  const currentValue = fieldProps.value
  const error = meta.error
  const errorId = useErrorId({ error })
  const useUpdatedToolbarActions = props.useUpdatedToolbarActions
  // When updated toolbar actions are on, Restore default sits in the editor label row
  // so it can vertically align with FieldLabel (Figma CRUD mock).
  const fieldLabel = !hideLabel ? (
    <FieldLabel
      id={`${id}-label`}
      htmlFor={id}
      text={label}
      proTip={proTip}
      isRequired={isRequired}
    />
  ) : null

  const onChange = useCallback((value) => {
    if (value.source !== 'user') {
      return
    }
    if (props.disabled) { return }

    setValue(value)
    if (currentValue.delta && !meta.touched) setTouched(true)
    if (currentValue.delta && meta.error) setError(undefined)
  }, [currentValue, setTouched, setValue, setError, meta.error, meta.touched, props.disabled])

  const {
    openLinkPopup,
    linkPopupProps
  } = useSimpleLinkPopup()
  const {
    editorProps,
    tooltipProps
  } = useSimpleLinkTooltip({ onLinkEdit: openLinkPopup, useNarrowTooltip })

  return (
    <div className={className}>
      {!useUpdatedToolbarActions && fieldLabel}
      <BoxWithLinkTooltip
        useNarrowTooltip={useNarrowTooltip}
        {...tooltipProps}
      >
        <ControlledRichTextEditor
          {...props}
          {...fieldProps}
          id={id}
          label={useUpdatedToolbarActions ? fieldLabel : undefined}
          onChange={onChange}
          onLinkEdit={openLinkPopup}
          {...editorProps}
          aria-labelledby={`${id}-label`}
          aria-describedby={error}
          hasError={Boolean(error)}
        />
      </BoxWithLinkTooltip>
      <InsertLinkPopup
        {...linkPopupProps}
      />
      <FieldError text={error} id={errorId} />
    </div>
  )
}
